import { Component, Input, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { CustomSelectComponent } from 'components/input/custom-select/custom-select.component';
import { SubmitButtonComponent } from 'components/input/submit-button/submit-button.component';
import { DataRowComponent, DataTableComponent } from 'components/layout/data-table/data-table.component';
import { ListQueryComponent } from 'components/query/list-query.component';
import { ViewTaskflowComponent } from 'components/task/taskflow/view-taskflow.component';
import { AutocompleteOffDirective } from 'directives/autocomplete-off.directive';
import { InputStateDirective } from 'directives/input-state.directive';
import { ValidationDirective } from 'directives/validation.directive';
import { InputType } from 'enums/InputType';
import { QueryType } from 'enums/QueryType';
import { TaskActionType } from 'enums/TaskActionType';
import { QueryModel } from 'models/generated/Entities.GormCrm.Query';
import { QueryTaskActionModel } from 'models/generated/Entities.GormCrm.QueryTaskAction';
import { QueryTaskflowModel } from 'models/generated/Entities.GormCrm.QueryTaskflow';
import { DynamicQueryModel } from 'models/generated/Models.DynamicQuery';
import { Comparison, ExpressionFilterModel } from 'models/generated/Models.ExpressionFilter';
import { QueryPropertyModel, QueryPropertyType } from 'models/generated/Models.QueryProperty';
import { SelectListItemModel } from 'models/SelectList';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { TranslatePipe } from 'pipes/translate.pipe';
import { FormValidationService } from 'services/form-validation.service';
import { ComponentUrl, NavigationService, UrlParam } from 'services/navigation.service';
import { NotificationService } from 'services/notification.service';
import { QueryService } from 'services/query.service';
import { RelationService } from 'services/relation.service';
import { QueryInputComponent } from './query-input.component';


@Component({
	selector: 'editquery',
	templateUrl: './edit-query.component.html',
	styleUrls: ['./edit-query.component.scss'],
	providers: [FormValidationService],
	imports: [Layout, DataTableComponent, DataRowComponent, CustomInputComponent, CustomSelectComponent, InputStateDirective, FormsModule, ValidationDirective, AutocompleteOffDirective, SubmitButtonComponent, QueryInputComponent, ListView, TranslatePipe]
})
@ComponentUrl('edit-query')
export class EditQueryComponent {

	@ViewChild('runQueryButton') runQueryButton?: SubmitButtonComponent;

	public title = '';
	public query = new QueryModel();

	inputTypeEnum = InputType;
	queryTypeEnum = QueryType;
	taskActionTypeEnum = TaskActionType;
	queryTypeList: SelectListItemModel[] = [];
	_queryData = new DynamicQueryModel();
	set queryData(data: DynamicQueryModel) {
		this._queryData = data;
	}
	get queryData() { return this._queryData; }

	queryProperties?: QueryPropertyModel[];
	queryPropertiesList: SelectListItemModel[] = [];
	queryType?: QueryType;

	operatorType = Comparison;
	queryPropertyType = QueryPropertyType;
	results?: (QueryTaskActionModel | QueryTaskflowModel)[];

	@UrlParam
	@Input()
	set queryId(id: string | undefined) {
		if (id != undefined) {
			this.queryService.getQuery(id).then(query => {
				this.query = query;
				this.loadQuery();
			});
		} else {
			this.loadQuery();
		}
	}
	get queryId() {
		return this.query && this.query.id ? this.query.id : '';
	}

	constructor(
		private readonly navigation: NavigationService,
		private readonly queryService: QueryService,
		private readonly translate: TranslatePipe,
		private readonly notificationService: NotificationService) {

		this.queryTypeList = [];
		let queryTypeList: SelectListItemModel[] = [];
		QueryType.getKeys().forEach((value: string, key: QueryType) => {
			queryTypeList.push(new SelectListItemModel(key.toString(),
				this.translate.transform('enum.query-type.' + value)));
		});
		this.queryTypeList = queryTypeList;
	}

	loadQuery() {
		this.title = this.translate.transform(this.query.id ? 'query.edit-query' : 'query.create-query');
		this.queryData = new DynamicQueryModel();
		if (this.query.data != undefined && this.query.data != '') {
			this.queryData = RelationService.instantiateModelsFromData(new DynamicQueryModel(), Object.assign(this.queryData, JSON.parse(this.query.data)));
			this.queryType = this.queryData!.queryType;
		} else {
			this.resetFilter();
		}
		this.loadProperties();
		this.navigation.refreshUrl();
		this.navigation.openParentView(this, 'list-query', ListQueryComponent)
			.then(view => { view.selectedQuery = this.query; });
	}

	loadProperties() {
		if (this.queryData && this.queryType != undefined) {
			if (this.queryData.queryType == undefined) this.queryData.queryType = this.queryType;
			if (!this.query.id) this.resetFilter();
			this.queryService.getQueryProperties(this.queryType)
				.then(properties => {
					this.queryProperties = properties;
					this.queryPropertiesList = [];
					properties.forEach(property => {
						this.queryPropertiesList.push(new SelectListItemModel(property.name,
							this.translate.transform('query.' + QueryType[this.queryType!] + '.property.' + property.name)));
					});
					this.queryData!.filters.forEach(filter => {
						this.setFilterOperatorsList(filter);
					});
				});
		}
	}

	resetFilter() {
		this.queryData!.filters = [];
		this.addFilter();
	}

	addFilter() {
		const filter = new ExpressionFilterModel();
		filter.operatorsList = [];
		filter.property = new QueryPropertyModel();
		this.queryData!.filters.push(filter);
	}
	setFilterProperty(filter: ExpressionFilterModel, propertyName: string) {
		filter.property = this.queryProperties!.find(qp => qp.name == propertyName);
		this.setFilterOperatorsList(filter);
	}


	setFilterOperatorsList(filter: ExpressionFilterModel) {
		if (!this.queryProperties) return;
		let property = this.queryProperties.find(p => p.name === filter.property!.name);
		if (!property) return;
		let operatorsList: SelectListItemModel[] = [];
		let comparisonsList: Comparison[] = [];
		switch (property.type) {
			case QueryPropertyType.Text:
				comparisonsList.push(Comparison.Equal);
				comparisonsList.push(Comparison.Contains);
				comparisonsList.push(Comparison.StartsWith);
				comparisonsList.push(Comparison.EndsWith);
				break;
			case QueryPropertyType.Numeric:
				comparisonsList.push(Comparison.Equal);
				comparisonsList.push(Comparison.NotEqual);
				comparisonsList.push(Comparison.GreaterThan);
				comparisonsList.push(Comparison.GreaterThanOrEqual);
				comparisonsList.push(Comparison.LessThan);
				comparisonsList.push(Comparison.LessThanOrEqual);
				break;
			case QueryPropertyType.Date:
				comparisonsList.push(Comparison.Equal);
				comparisonsList.push(Comparison.NotEqual);
				comparisonsList.push(Comparison.GreaterThan);
				comparisonsList.push(Comparison.GreaterThanOrEqual);
				comparisonsList.push(Comparison.LessThan);
				comparisonsList.push(Comparison.LessThanOrEqual);
				comparisonsList.push(Comparison.Range);
				break;
			default:
				comparisonsList.push(Comparison.Equal);
				break;
		}
		if (property.nullable) {
			comparisonsList.push(Comparison.NotNull);
			comparisonsList.push(Comparison.Null);
		}
		
		comparisonsList.forEach(comparison => {
			operatorsList.push(new SelectListItemModel(comparison.toString(), this.translate.transform('query.operator.' + Comparison[comparison])));
		});
		
		filter.operatorsList = operatorsList.map(item => Object.assign(new SelectListItemModel(item.value, item.name), item));;
	}

	removeFilter(index: number) {
		this.queryData!.filters.splice(index, 1);
	}

	updateQuery() {
		this.queryData.queryType = this.queryType!;
		this.queryData = RelationService.instantiateModelsFromData(new DynamicQueryModel(), this.queryData);
		this.queryData.include(qd => qd.filters);
		this.queryData.filters.forEach(filter => filter.include(f => f.property));
	}

	saveQuery() {
		if (!this.queryData || this.queryType == undefined) return;
		this.updateQuery();

		this.queryService.saveQuery(this.query, this.queryData).then(query => {
			this.navigation.refreshView(EditQueryComponent).then(view => view.queryId = query.id ? query.id : '');
		});
	}

	runQuery() {
		this.updateQuery();
		this.results = undefined;

		if (this.queryData.queryType == QueryType.TaskAction) {
			this.queryService.executeFilterQuery<QueryTaskActionModel[]>(this.queryData).then(results => {
				this.results = [...results];
				this.runQueryButton!.reset();
			}).catch((error: Error) => this.queryError(error));
		}
		if (this.queryData.queryType == QueryType.Taskflow) {
			this.queryService.executeFilterQuery<QueryTaskflowModel[]>(this.queryData).then(results => {
				this.results = [...results];
				this.runQueryButton!.reset();
			}).catch((error: Error) => this.queryError(error));
		}
	}

	navigateToTaskflow(result: QueryTaskActionModel | QueryTaskflowModel) {
		this.navigation.openRelativeView(this, 'query-views', ViewTaskflowComponent).then(view => {
			view.taskflowId = result.taskflowId!;
			if (result.taskActionId) view.selectedActionId = result.taskActionId;
		});
	}

	queryError(error: Error) {
		this.runQueryButton!.reset();
		this.notificationService.error('query.execute-error', error.message);
	}

	getOptions(filter: ExpressionFilterModel): { [key: string]: string } {
		if (!this.queryProperties) return {};
		var queryProperty = this.queryProperties.find(queryProperty => queryProperty.name == filter.property!.name);
		if (!queryProperty) return {};
		return queryProperty.options;
	}

	isNullOperator(comparison: Comparison): boolean {
		return (comparison == Comparison.Null || comparison == Comparison.NotNull);
	}

	canSearch() {
		if (this.queryData.filters.some(filter => filter.property!.name == ''))
			return false;
		return true;
	}

}
